import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CartPanel from '@/components/CartPanel';
import { publicApi, clickCollectApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { ShoppingBag, Plus, CalendarIcon, Clock, Leaf, Flame } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function MenuItemCard({ item, addToCart, formatPrice }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-garden-800/5 shadow-sm menu-card" data-testid={`order-item-${item.id}`}>
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-heading font-semibold text-lg text-garden-800 flex-1">{item.name}</h3>
        {(!item.variants || item.variants.length === 0) && (
          <span className="font-heading font-bold text-lg text-garden-800 ml-2">{formatPrice(item.price)}</span>
        )}
      </div>
      {item.description && (
        <p className="text-sm text-charcoal/70 mb-3 leading-relaxed line-clamp-2">{item.description}</p>
      )}
      {item.tags && item.tags.length > 0 && (
        <div className="flex gap-1.5 mb-3">
          {item.tags.map((t) => (
            <Badge key={t} variant="secondary" className="text-xs bg-garden-100 text-garden-800 border-0">
              {t === 'végétarien' && <Leaf size={10} className="mr-1" />}
              {t === 'epicé' && <Flame size={10} className="mr-1" />}
              {t}
            </Badge>
          ))}
        </div>
      )}
      {item.variants && item.variants.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {item.variants.map((v) => (
            <Button
              key={v.name}
              onClick={() => addToCart(item, v.name)}
              size="sm"
              data-testid={`order-add-${item.id}-${v.name}`}
              className="bg-garden-100 text-garden-800 hover:bg-garden-200 rounded-full text-xs"
            >
              <Plus size={12} className="mr-1" /> {v.name} — {formatPrice(v.price)}
            </Button>
          ))}
        </div>
      ) : (
        <Button
          onClick={() => addToCart(item)}
          size="sm"
          data-testid={`order-add-${item.id}`}
          className="bg-garden-800 hover:bg-garden-900 text-white rounded-full text-sm mt-1"
        >
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
      )}
    </div>
  );
}

function CheckoutStep({ date, setDate, slots, slotsLoading, selectedSlot, setSelectedSlot, form, setForm, notes, setNotes, loading, handleCheckout, setStep, cartTotal, formatPrice }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
        <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4 flex items-center gap-2">
          <CalendarIcon size={20} className="text-garden-500" /> Retrait
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-sm mb-2 block">Date de retrait *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="order-date-picker" className="w-full justify-start border-garden-200 rounded-xl h-10">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'EEEE d MMMM', { locale: fr }) : 'Choisir une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} locale={fr} />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-sm mb-2 block">Créneau de retrait *</Label>
            {slotsLoading ? (
              <div className="flex items-center gap-2 text-sm text-charcoal/50">
                <Clock size={16} className="animate-spin" /> Chargement...
              </div>
            ) : !date ? (
              <p className="text-sm text-charcoal/50">Sélectionnez d'abord une date</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-red-500">Aucun créneau disponible ce jour</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    onClick={() => setSelectedSlot(s.time)}
                    data-testid={`order-slot-${s.time}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedSlot === s.time
                        ? 'bg-garden-800 text-white'
                        : 'bg-garden-100 text-garden-800 hover:bg-garden-200'
                    }`}
                  >
                    <Clock size={12} className="inline mr-1" /> {s.time}
                    <span className="text-xs ml-1 opacity-60">({s.available} places)</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
        <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4">Vos coordonnées</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm mb-1 block">Nom *</Label>
            <Input data-testid="order-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="border-garden-200 rounded-xl" placeholder="Votre nom" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Téléphone *</Label>
            <Input data-testid="order-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="border-garden-200 rounded-xl" placeholder="06 ..." />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-sm mb-1 block">Email *</Label>
          <Input type="email" data-testid="order-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="border-garden-200 rounded-xl" placeholder="votre@email.com" />
        </div>
        <div className="mt-4">
          <Label className="text-sm mb-1 block">Notes (optionnel)</Label>
          <Input data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="border-garden-200 rounded-xl" placeholder="Instructions spéciales..." />
        </div>
      </div>
      <div className="flex gap-4">
        <Button variant="outline" onClick={() => setStep('menu')} className="rounded-full border-garden-200">
          Modifier la commande
        </Button>
        <Button
          onClick={handleCheckout}
          disabled={loading}
          data-testid="order-pay-btn"
          className="flex-1 bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-lg font-medium shadow-lg"
        >
          {loading ? 'Redirection...' : `Payer ${formatPrice(cartTotal)}`}
        </Button>
      </div>
    </div>
  );
}

export default function Order() {
  const [menu, setMenu] = useState({ categories: [], formules: [] });
  const [ccSettings, setCcSettings] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [step, setStep] = useState('menu');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time init on mount; stable module-level API refs
    publicApi.getMenu().then((r) => {
      setMenu(r.data);
      if (r.data.categories && r.data.categories.length > 0) {
        setActiveCategory(r.data.categories[0].id);
      }
    }).catch(() => {});
    clickCollectApi.getSettings().then((r) => setCcSettings(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    clickCollectApi.getSlots(format(date, 'yyyy-MM-dd'))
      .then((r) => { setSlots(r.data.slots || []); setSelectedSlot(''); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const addToCart = (item, variantName) => {
    setCart((prev) => {
      const key = item.id + '-' + (variantName || 'default');
      const existing = prev.find((c) => c.key === key);
      if (existing) {
        return prev.map((c) => c.key === key ? { ...c, quantity: c.quantity + 1 } : c);
      }
      const price = variantName
        ? (item.variants.find((v) => v.name === variantName) || {}).price || item.price
        : item.price;
      return [...prev, { key, item_id: item.id, name: item.name, variant_name: variantName || null, price, quantity: 1 }];
    });
    toast.success((item.name) + (variantName ? ' (' + variantName + ')' : '') + ' ajouté');
  };

  const updateQty = (key, delta) => {
    setCart((prev) => prev
      .map((c) => c.key === key ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c)
      .filter((c) => c.quantity > 0)
    );
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const cartCount = useMemo(() => {
    return cart.reduce((sum, c) => sum + c.quantity, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const formatPrice = (p) => {
    if (p === null || p === undefined) return '';
    return Number.isInteger(p) ? p + ' €' : p.toFixed(2).replace('.', ',') + ' €';
  };

  const handleCheckout = async () => {
    if (cart.length === 0) { toast.error('Votre panier est vide'); return; }
    if (!date) { toast.error('Choisissez une date de retrait'); return; }
    if (!selectedSlot) { toast.error('Choisissez un créneau de retrait'); return; }
    if (!form.name || !form.email || !form.phone) { toast.error('Remplissez vos coordonnées'); return; }
    setLoading(true);
    try {
      const orderData = {
        items: cart.map((c) => ({ item_id: c.item_id, name: c.name, quantity: c.quantity, variant_name: c.variant_name })),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        pickup_date: format(date, 'yyyy-MM-dd'),
        pickup_time: selectedSlot,
        notes: notes,
        origin_url: window.location.origin,
      };
      const res = await clickCollectApi.createOrder(orderData);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la commande');
    }
    setLoading(false);
  };

  if (!ccSettings.enabled) {
    return (
      <div className="min-h-screen bg-cream" data-testid="order-page-disabled">
        <Navbar />
        <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 text-center">
          <ShoppingBag size={48} className="mx-auto text-garden-400 mb-4" />
          <h1 className="text-3xl font-heading font-bold text-garden-800 mb-4">Click & Collect</h1>
          <p className="text-charcoal/60">Le service de commande en ligne n'est pas disponible pour le moment.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const activeCat = menu.categories.find((c) => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-cream" data-testid="order-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">Click & Collect</span>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-garden-800">
                {step === 'menu' ? 'Commander en ligne' : 'Finaliser la commande'}
              </h1>
            </div>
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button data-testid="cart-toggle-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full relative">
                  <ShoppingBag size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-sunny text-charcoal text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream w-[320px] p-0">
                <CartPanel
                  cart={cart} cartTotal={cartTotal} updateQty={updateQty}
                  formatPrice={formatPrice} onCheckout={() => { setCartOpen(false); setStep('checkout'); }} step={step}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {step === 'menu' ? (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-none">
                    {menu.categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        data-testid={`order-category-${cat.slug}`}
                        className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all shrink-0 ${
                          activeCategory === cat.id
                            ? 'bg-garden-800 text-white shadow-md'
                            : 'bg-white text-charcoal hover:bg-garden-100 border border-garden-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  {activeCat && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {activeCat.items && activeCat.items.map((item) => (
                        <MenuItemCard key={item.id} item={item} addToCart={addToCart} formatPrice={formatPrice} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <CheckoutStep
                  date={date} setDate={setDate} slots={slots} slotsLoading={slotsLoading}
                  selectedSlot={selectedSlot} setSelectedSlot={setSelectedSlot}
                  form={form} setForm={setForm} notes={notes} setNotes={setNotes}
                  loading={loading} handleCheckout={handleCheckout} setStep={setStep}
                  cartTotal={cartTotal} formatPrice={formatPrice}
                />
              )}
            </div>
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <CartPanel
                  cart={cart} cartTotal={cartTotal} updateQty={updateQty}
                  formatPrice={formatPrice} onCheckout={() => setStep('checkout')} step={step}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
